/**
 * User Context Enrichment for CloudWatch RUM
 *
 * This module captures comprehensive user context data including:
 * - Device information (browser, OS, screen resolution, hardware)
 * - Network information (connection type, effective type, downlink, RTT)
 * - Geographic information (timezone, language, IP-based location)
 * - Session information (referrer, navigation type, history)
 * - Hardware capabilities (GPU, CPU cores, memory)
 * - Browser features and capabilities
 *
 * IP-based location is fetched asynchronously via ipapi.co
 */

export interface UserContextData {
  // Raw User Agent
  userAgent?: string;

  // Browser Information
  browser?: {
    name?: string;
    version?: string;
    vendor?: string;
    platform?: string;
    appName?: string;
    appVersion?: string;
  };

  // Device Information
  device?: {
    type?: string; // mobile, tablet, desktop
    vendor?: string;
    model?: string;
    hardwareConcurrency?: number; // CPU cores
    deviceMemory?: number; // GB
    maxTouchPoints?: number;
  };

  // Screen Information
  screen?: {
    width?: number;
    height?: number;
    availWidth?: number;
    availHeight?: number;
    colorDepth?: number;
    pixelRatio?: number;
    orientation?: string;
  };

  // Window/Viewport
  viewport?: {
    innerWidth?: number;
    innerHeight?: number;
    outerWidth?: number;
    outerHeight?: number;
  };

  // Operating System
  os?: {
    name?: string;
    version?: string;
  };

  // Network Information
  network?: {
    effectiveType?: string; // 4g, 3g, 2g, slow-2g
    downlink?: number; // Mbps
    rtt?: number; // ms
    saveData?: boolean;
    onLine?: boolean;
  };

  // Locale & Timezone
  locale?: {
    language?: string;
    languages?: string[];
    timezone?: string;
    timezoneOffset?: number;
    estimatedRegion?: string;
  };

  // Session & Navigation
  session?: {
    referrer?: string;
    referrerDomain?: string;
    landingPage?: string;
    currentPage?: string;
    sessionStart?: string;
    hostname?: string;
    protocol?: string;
    origin?: string;
    navigationType?: string;
    historyLength?: number;
  };

  // Document
  document?: {
    title?: string;
    characterSet?: string;
    visibilityState?: string;
  };

  // Memory (Chrome only)
  memory?: {
    jsHeapSizeLimit?: number;
    totalJSHeapSize?: number;
    usedJSHeapSize?: number;
  };

  // Features & Capabilities
  features?: {
    cookiesEnabled?: boolean;
    doNotTrack?: boolean;
    onLine?: boolean;
    touchEnabled?: boolean;
    webdriver?: boolean; // Bot detection
    hasWebGL?: boolean;
    hasServiceWorker?: boolean;
    hasGeolocation?: boolean;
    hasNotifications?: boolean;
  };

  // Hardware (GPU)
  hardware?: {
    gpuVendor?: string;
    gpuRenderer?: string;
  };

  // Date/Time context
  dateTime?: {
    dayOfWeek?: number;
    isWeekend?: boolean;
    hourOfDay?: number;
  };

  // IP-based Location (fetched async)
  ipLocation?: {
    ipAddress?: string;
    city?: string;
    region?: string;
    regionCode?: string;
    country?: string;
    countryCode?: string;
    continent?: string;
    postalCode?: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
    currency?: string;
    isp?: string;
    asn?: string;
  };

  // Geolocation (requires permission)
  geolocation?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    altitude?: number;
    altitudeAccuracy?: number;
    heading?: number;
    speed?: number;
  };
}

/**
 * Detect browser name and version from user agent
 */
function detectBrowser(): UserContextData['browser'] {
  if (typeof window === 'undefined' || !navigator) return {};

  const ua = navigator.userAgent;
  let name = 'Unknown';
  let version = 'Unknown';

  // Detect browser
  if (ua.indexOf('Firefox') > -1) {
    name = 'Firefox';
    const match = ua.match(/Firefox\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unknown';
  } else if (ua.indexOf('Edg') > -1) {
    name = 'Edge';
    const match = ua.match(/Edg\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unknown';
  } else if (ua.indexOf('Chrome') > -1) {
    name = 'Chrome';
    const match = ua.match(/Chrome\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unknown';
  } else if (ua.indexOf('Safari') > -1) {
    name = 'Safari';
    const match = ua.match(/Version\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unknown';
  } else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1) {
    name = 'IE';
    const match = ua.match(/(?:MSIE |rv:)(\d+\.\d+)/);
    version = match ? match[1] : 'Unknown';
  }

  return {
    appName: navigator.appName || 'Unknown',
    appVersion: navigator.appVersion || 'Unknown',
    name,
    platform: navigator.platform || 'Unknown',
    vendor: navigator.vendor || 'Unknown',
    version,
  };
}

/**
 * Detect operating system
 */
function detectOS(): UserContextData['os'] {
  if (typeof window === 'undefined' || !navigator) return {};

  const ua = navigator.userAgent;
  let name = 'Unknown';
  let version = 'Unknown';

  if (ua.indexOf('Win') > -1) {
    name = 'Windows';
    if (ua.indexOf('Windows NT 10.0') > -1) version = '10/11';
    else if (ua.indexOf('Windows NT 6.3') > -1) version = '8.1';
    else if (ua.indexOf('Windows NT 6.2') > -1) version = '8';
    else if (ua.indexOf('Windows NT 6.1') > -1) version = '7';
  } else if (ua.indexOf('Mac') > -1) {
    name = 'macOS';
    const match = ua.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
    version = match ? match[1].replace(/_/g, '.') : 'Unknown';
  } else if (ua.indexOf('Linux') > -1) {
    name = 'Linux';
    if (ua.indexOf('Ubuntu') > -1) version = 'Ubuntu';
    else if (ua.indexOf('Fedora') > -1) version = 'Fedora';
  } else if (ua.indexOf('Android') > -1) {
    name = 'Android';
    const match = ua.match(/Android (\d+\.?\d*)/);
    version = match ? match[1] : 'Unknown';
  } else if (
    ua.indexOf('iOS') > -1 ||
    ua.indexOf('iPhone') > -1 ||
    ua.indexOf('iPad') > -1
  ) {
    name = 'iOS';
    const match = ua.match(/OS (\d+_\d+)/);
    version = match ? match[1].replace(/_/g, '.') : 'Unknown';
  }

  return { name, version };
}

/**
 * Detect device type and hardware
 */
function detectDevice(): UserContextData['device'] {
  if (typeof window === 'undefined' || !navigator) return {};

  const ua = navigator.userAgent;
  let type = 'desktop';

  // Check for mobile/tablet
  if (/Mobi|Android/i.test(ua) && !/Tablet/i.test(ua)) {
    type = 'mobile';
  } else if (/Tablet|iPad/i.test(ua)) {
    type = 'tablet';
  }

  // Try to detect vendor
  let vendor = 'Unknown';
  if (ua.indexOf('Samsung') > -1) vendor = 'Samsung';
  else if (
    ua.indexOf('iPhone') > -1 ||
    ua.indexOf('iPad') > -1 ||
    ua.indexOf('Mac') > -1
  )
    vendor = 'Apple';
  else if (ua.indexOf('Pixel') > -1) vendor = 'Google';
  else if (ua.indexOf('OnePlus') > -1) vendor = 'OnePlus';
  else if (ua.indexOf('Huawei') > -1) vendor = 'Huawei';
  else if (ua.indexOf('Xiaomi') > -1) vendor = 'Xiaomi';

  return {
    // @ts-expect-error - deviceMemory is Chrome-specific
    deviceMemory: navigator.deviceMemory || undefined,
    hardwareConcurrency: navigator.hardwareConcurrency || undefined,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    type,
    vendor,
  };
}

/**
 * Get screen information
 */
function getScreenInfo(): UserContextData['screen'] {
  if (typeof window === 'undefined') return {};

  const screen = window.screen;
  let orientation = 'unknown';

  try {
    orientation =
      screen.orientation?.type ||
      (screen.width > screen.height ? 'landscape' : 'portrait');
  } catch {
    orientation = screen.width > screen.height ? 'landscape' : 'portrait';
  }

  return {
    availHeight: screen.availHeight,
    availWidth: screen.availWidth,
    colorDepth: screen.colorDepth,
    height: screen.height,
    orientation,
    pixelRatio: window.devicePixelRatio || 1,
    width: screen.width,
  };
}

/**
 * Get viewport/window information
 */
function getViewportInfo(): UserContextData['viewport'] {
  if (typeof window === 'undefined') return {};

  return {
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    outerHeight: window.outerHeight,
    outerWidth: window.outerWidth,
  };
}

/**
 * Get network information (if available)
 */
function getNetworkInfo(): UserContextData['network'] {
  if (typeof window === 'undefined' || !navigator) return { onLine: true };

  const connection =
    // @ts-expect-error - NetworkInformation is not in all browsers
    (navigator.connection ||
      // @ts-expect-error - mozConnection is Firefox-specific
      navigator.mozConnection ||
      // @ts-expect-error - webkitConnection is webkit-specific
      navigator.webkitConnection) as
      | {
          downlink?: number;
          effectiveType?: string;
          rtt?: number;
          saveData?: boolean;
        }
      | undefined;

  return {
    downlink: connection?.downlink || undefined,
    effectiveType: connection?.effectiveType || 'unknown',
    onLine: navigator.onLine,
    rtt: connection?.rtt || undefined,
    saveData: connection?.saveData || false,
  };
}

/**
 * Get locale and timezone information
 */
function getLocaleInfo(): UserContextData['locale'] {
  if (typeof window === 'undefined' || !navigator) return {};

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let estimatedRegion = 'Unknown';

  // Try to estimate region from timezone
  if (timezone) {
    const parts = timezone.split('/');
    if (parts.length >= 2) {
      estimatedRegion = parts[1].replace(/_/g, ' ');
    }
  }

  return {
    estimatedRegion,
    language: navigator.language,
    languages: navigator.languages
      ? Array.from(navigator.languages)
      : [navigator.language],
    timezone,
    timezoneOffset: new Date().getTimezoneOffset(),
  };
}

/**
 * Get session and navigation information
 */
function getSessionInfo(): UserContextData['session'] {
  if (typeof window === 'undefined') return {};

  let referrerDomain = '';
  if (document.referrer) {
    try {
      referrerDomain = new URL(document.referrer).hostname;
    } catch {
      referrerDomain = '';
    }
  }

  let navigationType = 'unknown';
  try {
    const navEntry = performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming;
    navigationType = navEntry?.type || 'unknown';
  } catch {
    // Fallback
  }

  return {
    currentPage: window.location.pathname,
    historyLength: window.history.length,
    hostname: window.location.hostname,
    landingPage: window.location.href,
    navigationType,
    origin: window.location.origin,
    protocol: window.location.protocol.replace(':', ''),
    referrer: document.referrer || undefined,
    referrerDomain: referrerDomain || 'direct',
    sessionStart: new Date().toISOString(),
  };
}

/**
 * Get document information
 */
function getDocumentInfo(): UserContextData['document'] {
  if (typeof document === 'undefined') return {};

  return {
    characterSet: document.characterSet || 'UTF-8',
    title: document.title || 'Unknown',
    visibilityState: document.visibilityState || 'visible',
  };
}

/**
 * Get memory information (Chrome only)
 */
function getMemoryInfo(): UserContextData['memory'] {
  if (typeof window === 'undefined') return undefined;

  // @ts-expect-error - performance.memory is Chrome-specific
  const memory = performance?.memory;

  if (!memory) return undefined;

  return {
    jsHeapSizeLimit: memory.jsHeapSizeLimit,
    totalJSHeapSize: memory.totalJSHeapSize,
    usedJSHeapSize: memory.usedJSHeapSize,
  };
}

/**
 * Detect browser features and capabilities
 */
function detectFeatures(): UserContextData['features'] {
  if (typeof window === 'undefined' || !navigator) return {};

  return {
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes',
    hasGeolocation: 'geolocation' in navigator,
    hasNotifications: 'Notification' in window,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasWebGL: !!document.createElement('canvas').getContext('webgl'),
    onLine: navigator.onLine,
    touchEnabled: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    webdriver: navigator.webdriver || false,
  };
}

/**
 * Detect GPU/hardware information via WebGL
 */
function detectHardware(): UserContextData['hardware'] {
  if (typeof window === 'undefined') return {};

  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return {
          gpuRenderer:
            gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'Unknown',
          gpuVendor:
            gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'Unknown',
        };
      }
    }
  } catch {
    // WebGL not available
  }

  return { gpuRenderer: 'Unknown', gpuVendor: 'Unknown' };
}

/**
 * Get date/time context
 */
function getDateTimeContext(): UserContextData['dateTime'] {
  const now = new Date();
  const dayOfWeek = now.getDay();

  return {
    dayOfWeek,
    hourOfDay: now.getHours(),
    isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
  };
}

/**
 * Capture comprehensive user context
 * This function should be called once when RUM initializes
 */
export function captureUserContext(): UserContextData {
  if (typeof window === 'undefined' || !navigator) {
    return {};
  }

  const context: UserContextData = {
    browser: detectBrowser(),
    dateTime: getDateTimeContext(),
    device: detectDevice(),
    document: getDocumentInfo(),
    features: detectFeatures(),
    hardware: detectHardware(),
    locale: getLocaleInfo(),
    memory: getMemoryInfo(),
    network: getNetworkInfo(),
    os: detectOS(),
    screen: getScreenInfo(),
    session: getSessionInfo(),
    userAgent: navigator.userAgent,
    viewport: getViewportInfo(),
  };

  return context;
}

/**
 * Fetch IP-based location data asynchronously
 * Uses ipapi.co - free tier allows 1000 requests/day
 * Caches result in sessionStorage (or localStorage as fallback) to minimize API calls
 */
export async function fetchIpLocation(): Promise<
  UserContextData['ipLocation'] | null
> {
  if (typeof window === 'undefined') return null;

  const CACHE_KEY = 'rum_ip_location';
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours for localStorage fallback

  // Helper to get storage
  const getStorage = () => {
    try {
      return window.sessionStorage;
    } catch {
      try {
        return window.localStorage;
      } catch {
        return null;
      }
    }
  };

  const storage = getStorage();
  if (!storage) return null; // No storage available

  try {
    // Check cache first
    const cached = storage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        // If using localStorage, check expiration
        if (storage === window.localStorage) {
          if (Date.now() - parsed.timestamp < CACHE_DURATION) {
            return parsed.data;
          }
          storage.removeItem(CACHE_KEY);
        } else {
          // sessionStorage doesn't need expiration check as it clears on session end
          return parsed.data;
        }
      } catch (_e) {
        // Invalid cache, ignore
        storage.removeItem(CACHE_KEY);
      }
    }

    const response = await fetch('https://ipapi.co/json/', {
      headers: { Accept: 'application/json' },
      method: 'GET',
    });

    if (!response.ok) {
      console.warn('Failed to fetch IP location:', response.status);
      return null;
    }

    const data = await response.json();

    const locationData = {
      asn: data.asn,
      city: data.city,
      continent: data.continent_code,
      country: data.country_name,
      countryCode: data.country_code,
      currency: data.currency,
      ipAddress: data.ip,
      isp: data.org,
      latitude: data.latitude,
      longitude: data.longitude,
      postalCode: data.postal,
      region: data.region,
      regionCode: data.region_code,
      timezone: data.timezone,
    };

    // Cache the result
    try {
      storage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: locationData,
          timestamp: Date.now(),
        }),
      );
    } catch (e) {
      console.warn('Failed to cache IP location:', e);
    }

    return locationData;
  } catch (error) {
    console.warn('Error fetching IP location:', error);
    return null;
  }
}

/**
 * Capture precise geolocation (requires user permission)
 */
export function captureGeolocation(): Promise<
  UserContextData['geolocation'] | null
> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude ?? undefined,
          altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
          heading: position.coords.heading ?? undefined,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          speed: position.coords.speed ?? undefined,
        });
      },
      () => {
        // User denied or error
        resolve(null);
      },
      { enableHighAccuracy: false, maximumAge: 300000, timeout: 5000 },
    );
  });
}

/**
 * Get a minimal subset of user context for every event
 */
export function getMinimalUserContext(): Partial<UserContextData> {
  if (typeof window === 'undefined') return {};

  return {
    network: {
      onLine: navigator.onLine,
    },
    session: {
      currentPage: window.location.pathname,
    },
    viewport: getViewportInfo(),
  };
}

/**
 * Global user context cache
 */
let userContextCache: UserContextData | null = null;

/**
 * Get cached user context or capture it
 */
export function getUserContext(): UserContextData {
  if (!userContextCache) {
    userContextCache = captureUserContext();
  }
  return userContextCache;
}

/**
 * Update cached context with IP location data
 */
export function updateContextWithIpLocation(
  ipLocation: UserContextData['ipLocation'],
) {
  if (userContextCache) {
    userContextCache.ipLocation = ipLocation;
  }
}

/**
 * Update session page context (call on route changes)
 */
export function updateSessionContext() {
  if (userContextCache && typeof window !== 'undefined') {
    userContextCache.session = {
      ...userContextCache.session,
      currentPage: window.location.pathname,
    };
    userContextCache.document = getDocumentInfo();
    userContextCache.dateTime = getDateTimeContext();
  }
}

/**
 * Flatten user context for RUM session attributes
 * AWS RUM session attributes must be flat key-value pairs
 */
export function flattenUserContext(
  context: UserContextData,
): Record<string, string | number | boolean> {
  const flat: Record<string, string | number | boolean> = {};

  // User Agent
  if (context.userAgent) flat.userAgent = context.userAgent;

  // Browser
  if (context.browser) {
    if (context.browser.name) flat.browser = context.browser.name;
    if (context.browser.version) flat.browserVersion = context.browser.version;
    if (context.browser.vendor) flat.browserVendor = context.browser.vendor;
    if (context.browser.platform) flat.platform = context.browser.platform;
    if (context.browser.appName) flat.appName = context.browser.appName;
    if (context.browser.appVersion)
      flat.appVersion = context.browser.appVersion;
  }

  // Device
  if (context.device) {
    if (context.device.type) flat.deviceType = context.device.type;
    if (context.device.vendor) flat.deviceVendor = context.device.vendor;
    if (context.device.hardwareConcurrency)
      flat.hardwareConcurrency = context.device.hardwareConcurrency;
    if (context.device.deviceMemory)
      flat.deviceMemory = context.device.deviceMemory;
    if (context.device.maxTouchPoints !== undefined)
      flat.maxTouchPoints = context.device.maxTouchPoints;
  }

  // OS
  if (context.os) {
    if (context.os.name) flat.osName = context.os.name;
    if (context.os.version) flat.osVersion = context.os.version;
  }

  // Screen
  if (context.screen) {
    if (context.screen.width) flat.screenWidth = context.screen.width;
    if (context.screen.height) flat.screenHeight = context.screen.height;
    if (context.screen.availWidth)
      flat.screenAvailWidth = context.screen.availWidth;
    if (context.screen.availHeight)
      flat.screenAvailHeight = context.screen.availHeight;
    if (context.screen.colorDepth) flat.colorDepth = context.screen.colorDepth;
    if (context.screen.pixelRatio) flat.pixelRatio = context.screen.pixelRatio;
    if (context.screen.orientation)
      flat.screenOrientation = context.screen.orientation;
  }

  // Viewport
  if (context.viewport) {
    if (context.viewport.innerWidth)
      flat.windowInnerWidth = context.viewport.innerWidth;
    if (context.viewport.innerHeight)
      flat.windowInnerHeight = context.viewport.innerHeight;
    if (context.viewport.outerWidth)
      flat.windowOuterWidth = context.viewport.outerWidth;
    if (context.viewport.outerHeight)
      flat.windowOuterHeight = context.viewport.outerHeight;
  }

  // Network
  if (context.network) {
    if (context.network.effectiveType)
      flat.networkType = context.network.effectiveType;
    if (context.network.downlink !== undefined)
      flat.networkDownlink = context.network.downlink;
    if (context.network.rtt !== undefined)
      flat.networkRtt = context.network.rtt;
    if (context.network.onLine !== undefined)
      flat.onLine = context.network.onLine;
  }

  // Locale
  if (context.locale) {
    if (context.locale.language) flat.language = context.locale.language;
    if (context.locale.languages)
      flat.languages = context.locale.languages.join(',');
    if (context.locale.timezone) flat.timezone = context.locale.timezone;
    if (context.locale.timezoneOffset !== undefined)
      flat.timezoneOffset = context.locale.timezoneOffset;
    if (context.locale.estimatedRegion)
      flat.estimatedRegion = context.locale.estimatedRegion;
  }

  // Session
  if (context.session) {
    if (context.session.referrer) flat.referrer = context.session.referrer;
    if (context.session.referrerDomain)
      flat.referrerDomain = context.session.referrerDomain;
    if (context.session.landingPage)
      flat.landingPage = context.session.landingPage;
    if (context.session.hostname) flat.hostname = context.session.hostname;
    if (context.session.protocol) flat.protocol = context.session.protocol;
    if (context.session.origin) flat.origin = context.session.origin;
    if (context.session.navigationType)
      flat.navigationType = context.session.navigationType;
    if (context.session.historyLength !== undefined)
      flat.historyLength = context.session.historyLength;
  }

  // Document
  if (context.document) {
    if (context.document.title) flat.documentTitle = context.document.title;
    if (context.document.characterSet)
      flat.characterSet = context.document.characterSet;
    if (context.document.visibilityState)
      flat.visibilityState = context.document.visibilityState;
  }

  // Features
  if (context.features) {
    if (context.features.cookiesEnabled !== undefined)
      flat.cookiesEnabled = context.features.cookiesEnabled;
    if (context.features.touchEnabled !== undefined)
      flat.touchEnabled = context.features.touchEnabled;
    if (context.features.doNotTrack !== undefined)
      flat.doNotTrack = context.features.doNotTrack;
    if (context.features.webdriver !== undefined)
      flat.webdriver = context.features.webdriver;
    if (context.features.hasWebGL !== undefined)
      flat.hasWebGL = context.features.hasWebGL;
    if (context.features.hasServiceWorker !== undefined)
      flat.hasServiceWorker = context.features.hasServiceWorker;
    if (context.features.hasGeolocation !== undefined)
      flat.hasGeolocation = context.features.hasGeolocation;
    if (context.features.hasNotifications !== undefined)
      flat.hasNotifications = context.features.hasNotifications;
  }

  // Hardware
  if (context.hardware) {
    if (context.hardware.gpuVendor) flat.gpuVendor = context.hardware.gpuVendor;
    if (context.hardware.gpuRenderer)
      flat.gpuRenderer = context.hardware.gpuRenderer;
  }

  // Date/Time
  if (context.dateTime) {
    if (context.dateTime.dayOfWeek !== undefined)
      flat.sessionDayOfWeek = context.dateTime.dayOfWeek;
    if (context.dateTime.isWeekend !== undefined)
      flat.sessionIsWeekend = context.dateTime.isWeekend;
  }

  // IP Location (if available)
  if (context.ipLocation) {
    if (context.ipLocation.ipAddress)
      flat.ipAddress = context.ipLocation.ipAddress;
    if (context.ipLocation.city) flat.ipCity = context.ipLocation.city;
    if (context.ipLocation.region) flat.ipRegion = context.ipLocation.region;
    if (context.ipLocation.regionCode)
      flat.ipRegionCode = context.ipLocation.regionCode;
    if (context.ipLocation.country) flat.ipCountry = context.ipLocation.country;
    if (context.ipLocation.countryCode)
      flat.ipCountryCode = context.ipLocation.countryCode;
    if (context.ipLocation.continent)
      flat.ipContinent = context.ipLocation.continent;
    if (context.ipLocation.postalCode)
      flat.ipPostalCode = context.ipLocation.postalCode;
    if (context.ipLocation.latitude !== undefined)
      flat.ipLatitude = context.ipLocation.latitude;
    if (context.ipLocation.longitude !== undefined)
      flat.ipLongitude = context.ipLocation.longitude;
    if (context.ipLocation.timezone)
      flat.ipTimezone = context.ipLocation.timezone;
    if (context.ipLocation.currency)
      flat.ipCurrency = context.ipLocation.currency;
    if (context.ipLocation.isp) flat.ipIsp = context.ipLocation.isp;
    if (context.ipLocation.asn) flat.ipAsn = context.ipLocation.asn;
  }

  return flat;
}
